import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Platform,
  Image,
  useWindowDimensions,
} from 'react-native';
import Svg, { Path, Circle, Line } from 'react-native-svg';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PTLanguageToggle from '../PerfectTruck/PTLanguageToggle';
import { FontFamily } from '../../constants/fonts';
import { AdminColors } from '../Admin/constants/adminColors';
import HarborActionTile, { HarborTileData } from './components/HarborActionTile';
import { RootState } from '../../store';
import { useDarkStyles, useDarkColor } from '../../theme/useDarkStyles';

interface HarborLandingProps {
  onNavigate: (screen: string, params?: { [key: string]: any }) => void;
}

// ---- Icons ----------------------------------------------------------------

const PerfectTruckSvgIcon = () => (
  <Image
    source={require('../../assets/perfectTruckpng.png')}
    style={baseStyles.tileIcon}
    resizeMode="contain"
  />
);

const DeliveryRPMSvgIcon = () => (
  <Image
    source={require('../../assets/deliveryRPM.png')}
    style={baseStyles.tileIcon}
    resizeMode="contain"
  />
);

// ---- Anchor icon (matches design nav i-anchor symbol) ---------------------

const AnchorIcon = () => {
  const dc = useDarkColor();
  return (
    <Svg
      width={30}
      height={30}
      viewBox="0 0 24 24"
      fill="none"
      stroke={dc('#034895')}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <Circle cx="12" cy="5.5" r="2.5" />
      <Line x1="12" y1="8" x2="12" y2="21" />
      <Line x1="7.5" y1="11" x2="16.5" y2="11" />
      <Path d="M4 14a8 8 0 0 0 16 0" />
    </Svg>
  );
};

// ---- Main component -------------------------------------------------------

const HarborLanding: React.FC<HarborLandingProps> = ({ onNavigate }) => {
  const styles = useDarkStyles(baseStyles);
  const { t } = useTranslation('harbor');
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const roleName = useSelector((state: RootState) => state.appData?.userInfo?.roleName);

  // Perfect Truck is available to all Harbor users; Delivery RPM is super-user only.
  const showPerfectTruck = true;
  const showDeliveryRPM = roleName === 'Super User';

  const allTiles: HarborTileData[] = [
    {
      id: 'perfect-truck',
      title: t('landing.perfect_truck_tile.title'),
      description: t('landing.perfect_truck_tile.description'),
      roles: t('landing.perfect_truck_tile.roles'),
      iconBg: AdminColors.blue700,
      iconBg2: AdminColors.blue800,
      Icon: PerfectTruckSvgIcon,
      screen: 'Perfect Truck',
      screenParams: { fromHarbor: true },
    },
    {
      id: 'delivery-rpm',
      title: t('landing.delivery_rpm_tile.title'),
      description: t('landing.delivery_rpm_tile.description'),
      roles: t('landing.delivery_rpm_tile.roles'),
      iconBg: '#1B5E20',
      iconBg2: '#2E7D32',
      Icon: DeliveryRPMSvgIcon,
      screen: 'Delivery RPM',
    },
  ];

  const visibleTiles = allTiles.filter((tile) => {
    if (tile.id === 'perfect-truck') return showPerfectTruck;
    if (tile.id === 'delivery-rpm') return showDeliveryRPM;
    return false;
  });

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      {/* Breadcrumb */}
      <View style={styles.breadcrumb}>
        <Text style={styles.breadcrumbCurrent}>{t('breadcrumb_harbor')}</Text>
      </View>

      {/* Page header with language toggle */}
      <View style={[styles.pageHead, styles.pageHeadRow]}>
        <View style={{ flex: 1 }}>
          <View style={styles.pageTitleRow}>
            <AnchorIcon />
            <Text style={styles.pageTitle}>{t('landing.page_title')}</Text>
          </View>
          <Text style={styles.pageSubtitle}>{t('landing.subtitle')}</Text>
        </View>
        <PTLanguageToggle compact={isMobile} />
      </View>

      {/* Tile grid */}
      {visibleTiles.length === 0 ? (
        <View style={styles.emptyState}>
          <Text style={styles.emptyTitle}>{t('landing.empty_state_title')}</Text>
          <Text style={styles.emptyBody}>{t('landing.empty_state_body')}</Text>
        </View>
      ) : (
        <View style={[styles.tileGrid, isMobile && styles.tileGridMobile]}>
          {visibleTiles.map((tile) => (
            <HarborActionTile key={tile.id} tile={tile} onNavigate={onNavigate} isMobile={isMobile} />
          ))}
        </View>
      )}
    </ScrollView>
  );
};

// ---- Styles ---------------------------------------------------------------

const baseStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: AdminColors.bgPage,
  },
  scrollContent: {
    padding: 24,
    paddingBottom: 64,
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  breadcrumbCurrent: {
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: '#2D2D2D',
    fontWeight: '500',
  },
  pageHead: {
    marginBottom: 18,
  },
  pageHeadRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
  },
  pageTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 6,
  },
  pageTitle: {
    fontSize: 28,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#2D2D2D',
  },
  pageSubtitle: {
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#616161',
    marginBottom: 18,
  },
  tileGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
  },
  tileGridMobile: {
    flexDirection: 'column',
  },
  tileIcon: {
    height: 58,
    width: 130,
  },
  emptyState: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 40,
    alignItems: 'center',
    ...Platform.select({
      ios: {
        shadowColor: '#0055BC',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 8,
      },
      android: {
        elevation: 2,
      },
    }),
  },
  emptyTitle: {
    fontSize: 18,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#1A1A1A',
    marginBottom: 8,
  },
  emptyBody: {
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#616161',
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 440,
  },
});

export default HarborLanding;
